import React from 'react';
import { AlertTriangle, ArrowRight, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { inr, pct } from './format';

export const MrpMismatchBanner = ({ items, onView, onFilterAll }) => {
  if (!items?.length) return null;
  const products = new Set(items.map((i) => i.sku)).size;
  return (
    <div className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="mrp-mismatch-banner">
      <AlertTriangle size={18} className="text-rose-600 shrink-0" />
      <div className="flex-1 min-w-[12rem] text-sm text-rose-800">
        <span className="font-bold" data-testid="mrp-mismatch-count">{items.length}</span> GRN line(s) across <span className="font-bold">{products}</span> product(s) have a PO MRP ≠ GRN MRP mismatch — verify pricing before billing.
      </div>
      <Button size="sm" variant="outline" className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-100" onClick={onFilterAll} data-testid="mrp-mismatch-filter-all"><Filter size={13} />Filter dashboard</Button>
      <Button size="sm" className="gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={onView} data-testid="mrp-mismatch-view"><ArrowRight size={13} />View alerts</Button>
    </div>
  );
};

export const MrpMismatchPanel = ({ items, onPick }) => {
  if (!items?.length) return null;
  return (
    <div id="mrp-mismatch-panel" className="rounded-xl border border-rose-200 bg-white overflow-hidden" data-testid="mrp-mismatch-panel">
      <div className="px-4 py-3 border-b border-rose-100 bg-rose-50/60 flex items-center gap-2">
        <AlertTriangle size={15} className="text-rose-600" />
        <span className="text-sm font-semibold text-slate-800">MRP Mismatch Alerts</span>
        <span className="text-[11px] text-slate-500">Click a row to filter the dashboard to that product & GRN</span>
      </div>
      <div className="overflow-auto max-h-80">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-900 text-slate-100">
            <tr>{['Product ID / SKU', 'Product Name', 'Supplier', 'PO Number', 'GRN Number', 'GRN Date', 'PO MRP', 'GRN MRP', 'Difference', 'Change %'].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={`${r.grn_number}-${r.sku}-${i}`} onClick={() => onPick(r)}
                className="border-t border-slate-100 cursor-pointer hover:bg-rose-50 transition-colors" data-testid="mrp-mismatch-row">
                <td className="px-3 py-2 font-medium text-slate-800">{r.sku}</td>
                <td className="px-3 py-2 text-slate-700">{r.product_name}</td>
                <td className="px-3 py-2 text-slate-600">{r.supplier_name}</td>
                <td className="px-3 py-2">{r.po_number}</td>
                <td className="px-3 py-2">{r.grn_number}</td>
                <td className="px-3 py-2">{r.grn_date}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(r.po_mrp)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(r.grn_mrp)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.mrp_variance > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{inr(r.mrp_variance)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${r.mrp_variance > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{pct(r.mrp_change_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
